import React from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import Badge from "@/components/ui/badge";
import Text, { TextVariant } from "@/components/ui/text";
import { OnboardingStatus } from "@/pages/contracts/types";
import { cn } from "@/utils/utils";

interface Props {
  value: OnboardingStatus | null | undefined;
  valueVariant: TextVariant;
}

const EMPTY_VALUE = "-";

const STATUS_LABEL_KEYS: Record<OnboardingStatus, string> = {
  [OnboardingStatus.NOT_STARTED]: "contracts.kpis.onboardingStatuses.notStarted",
  [OnboardingStatus.IN_PROGRESS]: "contracts.kpis.onboardingStatuses.inProgress",
  [OnboardingStatus.COMPLETED]: "contracts.kpis.onboardingStatuses.completed"
};

// Match the onboarding workflow badge palette so status colors stay consistent
// across the onboarding feature. Not-started keeps the neutral badge.
const STATUS_BADGE_STYLES: Record<
  OnboardingStatus,
  { className: string; textClassName: string }
> = {
  [OnboardingStatus.NOT_STARTED]: {
    className: "bg-badges-selected",
    textClassName: ""
  },
  [OnboardingStatus.IN_PROGRESS]: {
    className: "bg-[rgba(var(--surface-chips-natural-rgb),0.20)]",
    textClassName: "text-link"
  },
  [OnboardingStatus.COMPLETED]: {
    className: "bg-[rgba(var(--surface-chips-positive-rgb),0.18)]",
    textClassName: "text-charts-positive"
  }
};

const OnboardingStatusKPI: React.FC<Props> = ({ value, valueVariant }) => {
  const { t } = useTranslation();

  if (isNil(value)) {
    return <Text variant={valueVariant}>{EMPTY_VALUE}</Text>;
  }

  const labelKey = STATUS_LABEL_KEYS[value];
  // Unknown status strings fall back to the raw value rather than an empty badge.
  const label = labelKey ? t(labelKey) : value;
  // Unknown status strings keep the neutral badge rather than losing their color.
  const badgeStyle =
    STATUS_BADGE_STYLES[value] ??
    STATUS_BADGE_STYLES[OnboardingStatus.NOT_STARTED];

  return (
    <Badge className={cn("border-none", badgeStyle.className)}>
      <Text variant="smBody" className={badgeStyle.textClassName}>
        {label}
      </Text>
    </Badge>
  );
};

export default OnboardingStatusKPI;
